import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PaperPlaneTilt, PaperPlaneRight, Eye, WarningCircle, FileText, FloppyDisk, BookmarkSimple } from "@phosphor-icons/react";
import { TestSendDialog } from "./TestSendDialog";
import { RecipientPicker } from "./RecipientPicker";
import { EmailEditor } from "./EmailEditor";
import { TemplatePicker } from "./TemplatePicker";
import { SaveTemplateDialog } from "./SaveTemplateDialog";
import { TemplateFormDialog } from "./TemplateFormDialog";
import { ReviewDialog } from "./ReviewDialog";
import { PreviewPanel } from "./PreviewPanel";
import { ST, FollowupStatus } from "./CampaignParts";
import { SentRecipients } from "./SentRecipients";
import { unknownVars, VARS } from "./render";

const Section = ({ n, title, children, right, testid }) => (
  <section className="surface rounded-lg p-4 md:p-5 space-y-3" data-testid={testid}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-sm md:text-base font-semibold text-fg flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-accent/15 text-accent text-xs flex items-center justify-center">{n}</span>{title}</h2>
      {right}
    </div>
    {children}
  </section>
);

export function CampaignEditor({ id, onBack, onOpen }) {
  const [c, setC] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [dlg, setDlg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tplKey, setTplKey] = useState(0);
  const [fu, setFu] = useState({ enabled: false, delay_days: 4, subject: "", body_html: "" });
  useEffect(() => { api.get("/admin/leads/followup-template").then(({ data }) => setFu((f) => ({ ...f, delay_days: data.delay_days, body_html: data.body_html }))).catch(() => {}); }, []);
  const load = useCallback(() => api.get(`/admin/leads/campaigns/${id}`).then(({ data }) => { setC(data); setDirty(false); return data; })
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (c?.status === "sending") { const t = setInterval(load, 3000); return () => clearInterval(t); } return undefined; }, [c?.status, load]);
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);
  if (!c) return <div className="text-muted text-sm animate-pulse">Chargement…</div>;

  const draft = c.status === "draft";
  const patch = (p) => { setC((x) => ({ ...x, ...p })); setDirty(true); };
  const save = async (silent) => {
    try {
      await api.put(`/admin/leads/campaigns/${id}`, { name: c.name, subject: c.subject, body_html: c.body_html, selection: c.selection,
        template_id: c.template?.id || null, template_name: c.template?.name || "" });
      if (!silent) toast.success("Brouillon enregistré");
      return await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return null; }
  };
  const open = async (kind) => {
    const d = await save(true);
    if (!d) return;
    if (kind === "review" && !d.recipients.length) { toast.error("Aucun destinataire sélectionné avec une adresse valide."); return; }
    setDlg(kind);
  };
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/leads/campaigns/${id}/send`, { confirm: true, expected_count: c.recipients.length, expected_keys: c.recipients.map((r) => r.key),
        followup: fu.enabled ? { ...fu, subject: fu.subject || `Re : ${c.subject}`, delay_days: Number(fu.delay_days) } : null });
      toast.success(`Envoi lancé vers ${c.recipients.length} destinataire(s)`); setDlg(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const applyTemplate = (t) => {
    patch({ subject: t.subject, body_html: t.body_html, template: { id: t.id, name: t.name } });
    setDlg(null);
    toast.success(`Template « ${t.name} » appliqué — vos modifications ne changeront pas le template.`);
  };
  const unknown = unknownVars(c.subject, c.body_html);
  const count = draft ? new Set(c.candidates.filter((x) => c.selection.includes(x.key) && !x.issue).map((x) => x.email)).size : c.recipients.length;

  return (
    <div className="space-y-4 pb-2" data-testid="lead-campaign-editor">
      <button onClick={() => (!dirty || window.confirm("Quitter sans enregistrer les modifications ?")) && onBack()} className="text-xs text-muted hover:text-fg" data-testid="lead-campaign-back">← Toutes les campagnes</button>
      <div className="flex flex-wrap items-center gap-3">
        <Input disabled={!draft} className="bg-surface-2 border-border max-w-md text-base font-semibold" value={c.name} onChange={(e) => patch({ name: e.target.value })} data-testid="lead-campaign-name" />
        <span className="text-xs border border-border rounded-full px-2.5 py-1 text-muted" data-testid="lead-campaign-status">{ST[c.status]}</span>
        {draft && <span className={`text-xs ${dirty ? "text-amber-500" : "text-muted"}`} data-testid="lead-campaign-dirty">{dirty ? "Modifications non enregistrées" : "Brouillon enregistré"}</span>}
      </div>
      {!c.sender && <p className="text-xs text-amber-500" data-testid="lead-campaign-no-sender">Aucune boîte d'envoi connectée sur votre compte admin : connectez-la dans <a href="/app/settings/email" className="underline">Paramètres → Email</a>.</p>}
      <FollowupStatus c={c} reload={load} />

      {draft ? (
        <>
          <Section n="1" title="Destinataires" testid="lead-campaign-recipients">
            <RecipientPicker candidates={c.candidates} selection={c.selection} onChange={(selection) => patch({ selection })} noContacts={c.companies_without_contacts} />
          </Section>
          <Section n="2" title="Message" testid="lead-campaign-message" right={
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setDlg("pick")} className="text-xs border border-border rounded-md px-3 py-1.5 flex items-center gap-1.5 hover:border-accent/40" data-testid="lead-campaign-choose-template"><FileText size={13} /> Choisir un template</button>
              <button onClick={() => setDlg("save-tpl")} className="text-xs border border-border rounded-md px-3 py-1.5 flex items-center gap-1.5 hover:border-accent/40" data-testid="lead-campaign-save-template"><BookmarkSimple size={13} /> Enregistrer comme template</button>
            </div>}>
            {c.template?.name && <p className="text-[11px] text-muted" data-testid="lead-campaign-template-origin">Basé sur le template « {c.template.name} ». Les modifications ci-dessous ne concernent que cette campagne.</p>}
            <Input className="bg-surface-2 border-border" placeholder="Objet de l'email" value={c.subject} onChange={(e) => patch({ subject: e.target.value })} data-testid="lead-campaign-subject" />
            <EmailEditor value={c.body_html} onChange={(body_html) => patch({ body_html })} testid="lead-campaign-body" />
            <p className="text-[11px] text-muted">Variables : {Object.entries(VARS).map(([k, l]) => `{{${k}}} = ${l.toLowerCase()}`).join(" · ")}. Signature de votre boîte d'envoi et pied de page d'opposition (« STOP ») ajoutés automatiquement.</p>
            {unknown.length > 0 && <p className="text-xs text-destructive flex gap-1" data-testid="lead-campaign-unknown-vars"><WarningCircle size={14} className="shrink-0" /> Variables inconnues : {unknown.map((v) => `{{${v}}}`).join(", ")} — elles seraient remplacées par du vide.</p>}
            <p className="text-[11px] text-muted flex gap-1"><WarningCircle size={13} className="shrink-0" /> Le message ne doit jamais évoquer une procédure, une difficulté ou l'absence de recrutement (contrôle automatique).</p>
          </Section>
          <div className="sticky bottom-0 z-20 surface rounded-lg border border-accent/30 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 shadow-lg" data-testid="lead-campaign-actionbar">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => open("review")} disabled={!count || !c.sender} data-testid="lead-campaign-validate-btn"
                className="text-xs bg-accent text-black font-semibold rounded-md px-4 py-2 flex items-center gap-1.5 disabled:opacity-50"><PaperPlaneTilt size={13} /> Vérifier et envoyer…</button>
              <button onClick={() => open("preview")} className="text-xs border border-border rounded-md px-3 py-2 flex items-center gap-1.5" data-testid="lead-campaign-preview-btn"><Eye size={13} /> Aperçu</button>
              <button onClick={() => open("test")} disabled={!c.sender} className="text-xs border border-border rounded-md px-3 py-2 flex items-center gap-1.5 disabled:opacity-50" data-testid="lead-campaign-test-send-btn"><PaperPlaneRight size={13} /> M'envoyer un test</button>
              <button onClick={() => save(false)} className="text-xs border border-border rounded-md px-3 py-2 flex items-center gap-1.5" data-testid="lead-campaign-save"><FloppyDisk size={13} /> Enregistrer</button>
            </div>
            <span className="text-sm text-fg"><b className="text-accent">{count}</b> destinataire(s) sélectionné(s)</span>
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <SentRecipients c={c} reload={load} onOpen={onOpen} />
          <div className="surface rounded-lg p-5"><div className="overline mb-2">Message envoyé</div><PreviewPanel cid={c.id} recipients={c.recipients} testid="lead-sent-preview" /></div>
        </div>
      )}

      <TemplatePicker open={dlg === "pick"} onClose={() => setDlg(null)} onApply={applyTemplate} onCreate={() => setDlg("new-tpl")} reloadKey={tplKey} />
      <TemplateFormDialog open={dlg === "new-tpl"} onClose={() => setDlg("pick")} onSaved={() => setTplKey((k) => k + 1)} />
      <SaveTemplateDialog open={dlg === "save-tpl"} onClose={() => setDlg(null)} subject={c.subject || ""} body={c.body_html || ""} source={c.template}
        onSaved={(t) => patch({ template: { id: t.id, name: t.name } })} />
      <Dialog open={dlg === "preview"} onOpenChange={() => setDlg(null)}>
        <DialogContent className="bg-surface border-border w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-campaign-preview">
          <DialogHeader><DialogTitle>Aperçu de l'email</DialogTitle></DialogHeader>
          <PreviewPanel cid={c.id} recipients={c.recipients} testid="lead-campaign-preview-panel" />
        </DialogContent>
      </Dialog>
      {draft && <ReviewDialog open={dlg === "review"} onClose={() => setDlg(null)} c={c} fu={fu} setFu={setFu} onSend={send} busy={busy} />}
      {draft && <TestSendDialog open={dlg === "test"} onClose={() => setDlg(null)} c={c} reload={load} />}
    </div>
  );
}
